import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";

interface AdminUser {
  id: string;
  fullName: string;
  username: string;
  email: string;
  phoneNumber: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  subcompanyId: string;
}

const DetailRow = ({ label, value }: { label: string; value: string | boolean }) => (
  <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
    </dd>
  </div>
);

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchUserData();
  }, [id]);
  ;
  const fetchUserData = async () => {
    try {
      const response = await axiosInstance.get(`/admins/${id}`);
      setUser(response.data);
    } catch (error) {
      setError("Failed to fetch user data");
      console.error("Error fetching user:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-md p-4">
          {error}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-600 rounded-md p-4">
          User not found
        </div>
      </div>
    );
  }

  return (
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-6">
          <PageBreadcrumb pageTitle="Admin User Details" />
          <div className="space-x-4">
            <button
              onClick={() => navigate(`/admin-user/edit/${id}`)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Edit
            </button>
            <button
              onClick={() => navigate("/admin-user")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Back to List
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="border-t border-gray-200">
            <dl>
              <DetailRow label="Full Name" value={user.fullName} />
              <DetailRow label="Username" value={user.username} />
              <DetailRow label="Email" value={user.email} />
              <DetailRow label="Phone Number" value={user.phoneNumber} />
              <DetailRow label="Subcompany ID" value={user.subcompanyId} />
              <DetailRow label="Is Active" value={user.isActive} />
              <DetailRow label="Is Super Admin" value={user.isSuperAdmin} />
            </dl>
          </div>
        </div>
      </div>
  );
} 